// src/components/auth/PrivateRoute.tsx
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { logDev } from '../../utils/logger';

interface PrivateRouteProps {
  children: React.ReactNode;
  skipPersonalizationCheck?: boolean;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  skipPersonalizationCheck = false
}) => {
  const { currentUser, loading, onboardingStatus } = useAuth();
  const location = useLocation();
  const isDevelopment = process.env.NODE_ENV === 'development';

  // 簡化的郵箱驗證檢查
  const shouldRedirectToVerification = React.useMemo(() => {
    if (!currentUser || isDevelopment || location.pathname === '/verify-email') {
      return false;
    }

    return Boolean(
      currentUser.email &&
      !currentUser.emailVerified &&
      !currentUser.email.endsWith('gmail.com')
    );
  }, [currentUser, isDevelopment, location.pathname]);

  // Loading 狀態：AuthContext loading 或 onboarding 檢查中
  if (loading || onboardingStatus.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  // 未登入檢查
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // 郵箱驗證檢查
  if (shouldRedirectToVerification) {
    return <Navigate to="/verify-email" replace />;
  }

  // 個人化設置檢查
  if (!skipPersonalizationCheck &&
      onboardingStatus.needsPersonalization &&
      !location.pathname.startsWith('/setup/')) {
    logDev('PrivateRoute', '重定向到個人化設置', {
      currentPath: location.pathname,
      reason: 'needsPersonalization=true'
    });
    return <Navigate to="/setup/intro" replace />;
  }

  return <>{children}</>;
};

export default PrivateRoute;